import React from "react";
import { createPortal } from "react-dom";
import { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftRight,
  BarChart3,
  CornerUpLeft,
  Eye,
  List,
  UserCog,
} from "../../../../components/icons";
import type { Formation } from "../../hooks/useTacticalPositions";
import { apiClient } from "../../../../lib/api";
import {
  lightenForContrast,
  relativeLuminance,
} from "../../../../lib/teamPalette";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match } from "../../types";
import { PlayerPicker, getTeamRosterForEvent } from "./PlayerPicker";

export type CockpitViewMode = "logger" | "analytics" | "review";

/** Minimal team info needed for the color picker. */
export interface TeamColorInfo {
  teamId: string;
  primary_color: string | null;
  secondary_color: string | null;
}

interface TeamSelectorProps {
  isFlipped: boolean;
  onFlip: () => void;
  onUndo: () => void;
  undoDisabled: boolean;
  undoCount?: number;
  disabled?: boolean;
  viewMode?: CockpitViewMode;
  setViewMode?: (mode: CockpitViewMode) => void;
  /**
   * @deprecated PMA-COCK-117 — the formation picker (preset/custom
   * selection, mid-match re-arrangement of on-field tactical positions via
   * `applyFormation` → `useTacticalPositions.ts`'s `resolveFormationPositions`)
   * is unlinked from the cockpit's render path at the client's explicit,
   * cost-informed request (2026-08-16) — a client-ratified capability loss,
   * not dead code: the client was told this removes the only in-cockpit way
   * to re-arrange the pitch to a new formation mid-match, and chose removal
   * anyway ("Sin Formación" read as clutter).
   *
   * The unlink point is NOT hardcoding these props to `undefined` at the
   * `LoggerView.tsx` call site — the original `{applyFormation && (...)}`
   * gate below wrapped the team short-name label and `ColorSwatchButton`
   * (PMA-COCK-023/041, an unrelated, still-live capability) in the SAME
   * conditional as the picker, so gating on `applyFormation` would have
   * silently taken the color swatch and team name down with it. Instead,
   * `<FormationPicker>` itself is the only thing removed from this
   * component's render below; the slot div, team name label, and
   * `ColorSwatchButton` render unconditionally now, same as if
   * `applyFormation` were always truthy (its real-world default — this is
   * `TeamSelector`'s only caller and always passed a real function before
   * this change). `homeFormation`/`awayFormation`/`applyFormation` stay in
   * the prop contract, still threaded from `LoggerView.tsx`, simply no
   * longer read anywhere in this component's JSX. `FormationPicker.tsx` and
   * `useTacticalPositions.ts`'s `applyFormation` are both kept fully
   * implemented, untouched, for a future caller that re-wires this prop —
   * same shape as PMA-COCK-110's drag-substitution deprecation.
   */
  homeFormation?: Formation | null;
  awayFormation?: Formation | null;
  applyFormation?: (side: "home" | "away", formation: Formation | null) => void;
  homeTeamName?: string;
  awayTeamName?: string;
  /** Color picker — pass team color info to enable the swatch button. */
  homeTeamColorInfo?: TeamColorInfo;
  awayTeamColorInfo?: TeamColorInfo;
  /** Called after a successful color patch so parent can refresh team state. */
  onTeamColorSaved?: (
    teamId: string,
    primary: string | null,
    secondary: string | null,
  ) => void;
  /** PMA-COCK-099: full match reference — used to resolve the roster for
   * the "change last event player" quick action. */
  match?: Match | null;
  /** Full live event list — walked back-to-front to find the most recent
   * goal / yellow / red card for one-tap player reassignment. Optional:
   * the quick-reassign button only renders once this, `match`, and
   * `onUpdateEventData` are all supplied. */
  liveEvents?: MatchEvent[];
  onUpdateEventData?: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void> | void;
  t: TFunction<"logger">;
}

/** Goal detection mirrors LiveEventFeed.tsx's `getDisplayType`: a Shot/
 * Header/Free Kick/Penalty event whose outcome is "Goal", or the
 * dedicated `OwnGoal` type. */
const isGoalMatchEvent = (event: MatchEvent): boolean =>
  event.data?.outcome === "Goal" || event.type === "OwnGoal";

const isYellowOrRedCardEvent = (event: MatchEvent): boolean => {
  if (event.type !== "Card") return false;
  const cardType = String(event.data?.card_type || "").toLowerCase();
  return cardType.includes("yellow") || cardType.includes("red");
};

/**
 * The event the "change last event player" quick action targets: the most
 * recent PERSISTED (has `_id`) goal / yellow / red card carrying a
 * `player_id` — client's own request (2026-08-09), covering exactly
 * "puede ser gol, tarjeta amarilla, tarjeta roja". Skips coaching-staff
 * card subjects (`staff_id` set, no `player_id`) — the picker only
 * resolves player_ids, same guard as LiveEventFeed's inline editor
 * (PMA-COCK-099); a staff card must not become reassignable to a player
 * through this button. `liveEvents` is chronological ascending
 * (push-appended in useMatchLogStore), so the scan runs from the end.
 */
const findLastPlayerGoalOrCardEvent = (
  events: MatchEvent[],
): MatchEvent | null => {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (!event._id || event.staff_id || !event.player_id) continue;
    if (isGoalMatchEvent(event) || isYellowOrRedCardEvent(event)) {
      return event;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// TeamColorPicker — inline popover for two <input type="color"> controls
// ---------------------------------------------------------------------------

interface TeamColorPickerProps {
  teamId: string;
  initialPrimary: string | null;
  initialSecondary: string | null;
  onClose: () => void;
  onSaved: (primary: string | null, secondary: string | null) => void;
  t: TFunction<"logger">;
  /** Viewport-relative anchor coords so the popover can use position:fixed */
  anchorRect: DOMRect;
}

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const TeamColorPicker = ({
  teamId,
  initialPrimary,
  initialSecondary,
  onClose,
  onSaved,
  t,
  anchorRect,
}: TeamColorPickerProps) => {
  const DEFAULT_PRIMARY = "#1d4ed8";
  const DEFAULT_SECONDARY = "#ffffff";

  const [primary, setPrimary] = useState(initialPrimary ?? DEFAULT_PRIMARY);
  const [secondary, setSecondary] = useState(
    initialSecondary ?? DEFAULT_SECONDARY,
  );
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click OR Escape.
  //
  // • pointerdown in the CAPTURE phase: fires before any stopPropagation called
  //   by field/player-token handlers, so clicking anywhere on the tactical field
  //   (even if those handlers stop propagation) reliably dismisses the popover.
  // • Deferred by one tick: prevents the button click that *opened* the popover
  //   from immediately closing it on the same pointer event.
  // • keydown on Escape: standard a11y close affordance.
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    const handlePointerDown = (e: PointerEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    timerId = setTimeout(() => {
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("keydown", handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const showLowContrast =
    HEX_PATTERN.test(primary) &&
    (() => {
      const l = relativeLuminance(primary);
      return l !== null && (l < 0.2 || l > 0.8);
    })();

  const handleApply = async () => {
    if (saving) return;
    setSaving(true);
    setToast(null);
    try {
      await apiClient.patch(`/teams/${teamId}`, {
        primary_color: primary,
        secondary_color: secondary,
      });
      setToast({ msg: t("teamColors.saved", "Colors saved"), ok: true });
      onSaved(primary, secondary);
      setTimeout(onClose, 800);
    } catch {
      setToast({
        msg: t("teamColors.errorSaving", "Failed to save colors"),
        ok: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (saving) return;
    setSaving(true);
    setToast(null);
    try {
      await apiClient.patch(`/teams/${teamId}`, {
        primary_color: null,
        secondary_color: null,
      });
      setToast({ msg: t("teamColors.saved", "Colors saved"), ok: true });
      onSaved(null, null);
      setTimeout(onClose, 800);
    } catch {
      setToast({
        msg: t("teamColors.errorSaving", "Failed to save colors"),
        ok: false,
      });
    } finally {
      setSaving(false);
    }
  };

  // Compute fixed position: appear just below the anchor swatch, clamped to
  // viewport width so it never overflows right.
  const POPOVER_WIDTH = 208; // w-52 = 13rem = 208px
  const GAP = 6;
  const rawLeft = anchorRect.left;
  const top = anchorRect.bottom + GAP;
  const left = Math.min(rawLeft, window.innerWidth - POPOVER_WIDTH - 8);

  return createPortal(
    <div
      ref={popoverRef}
      data-testid="team-color-picker-popover"
      style={{ position: "fixed", top, left, width: POPOVER_WIDTH }}
      className="z-[9999] bg-slate-900 border border-slate-600 rounded-xl shadow-2xl p-4 space-y-3"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {t("teamColors.title", "Team colors")}
      </p>

      <div className="space-y-2">
        <label className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-300">
            {t("teamColors.primaryColor", "Primary")}
          </span>
          <input
            type="color"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border border-slate-600 bg-transparent"
            aria-label={t("teamColors.primaryColor", "Primary color")}
          />
        </label>

        <label className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-300">
            {t("teamColors.secondaryColor", "Secondary")}
          </span>
          <input
            type="color"
            value={secondary}
            onChange={(e) => setSecondary(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer border border-slate-600 bg-transparent"
            aria-label={t("teamColors.secondaryColor", "Secondary color")}
          />
        </label>
      </div>

      {showLowContrast && (
        <p className="text-[10px] text-amber-400 leading-snug" role="alert">
          {t(
            "teamColors.lowContrastWarning",
            "Low contrast — text may be unreadable",
          )}
        </p>
      )}

      {toast && (
        <p
          className={`text-[11px] font-semibold ${
            toast.ok ? "text-emerald-400" : "text-red-400"
          }`}
          role="status"
        >
          {toast.msg}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={handleClear}
          disabled={saving}
          data-testid="team-color-picker-clear"
          className="flex-1 text-xs py-1.5 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          {t("teamColors.clear", "Clear")}
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={saving}
          data-testid="team-color-picker-apply"
          className="flex-1 text-xs py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-colors font-semibold"
        >
          {saving
            ? t("teamColors.saving", "Saving…")
            : t("teamColors.apply", "Apply")}
        </button>
      </div>
    </div>,
    document.body,
  );
};

// ---------------------------------------------------------------------------
// ColorSwatchButton — the tiny trigger next to each team name label
// ---------------------------------------------------------------------------

interface ColorSwatchButtonProps {
  colorInfo: TeamColorInfo;
  label: string;
  onSaved: (primary: string | null, secondary: string | null) => void;
  t: TFunction<"logger">;
}

export const ColorSwatchButton = ({
  colorInfo,
  label,
  onSaved,
  t,
}: ColorSwatchButtonProps) => {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSaved = useCallback(
    (primary: string | null, secondary: string | null) => {
      // Do NOT close here — TeamColorPicker's handleApply/handleClear call
      // setTimeout(onClose, 800) so the success toast has time to render
      // before the picker unmounts. Closing immediately would unmount the
      // picker before React paints the toast state.
      onSaved(primary, secondary);
    },
    [onSaved],
  );

  const handleToggle = useCallback(() => {
    if (!open && buttonRef.current) {
      // Capture the button's viewport-relative position before opening
      setAnchorRect(buttonRef.current.getBoundingClientRect());
    }
    setOpen((v) => !v);
  }, [open]);

  return (
    // EPIC-111 UIJ-R1-3: the BUTTON ITSELF is the 44x44 hit area (the
    // judge's touch-target metric measures the clickable element, not a
    // ::before pseudo-element or a non-clickable wrapper); the 16px dot is
    // an inner span carrying the team colour.
    <div className="relative flex items-center justify-center w-[44px] h-[44px] shrink-0">
      <button
        ref={buttonRef}
        type="button"
        data-testid={`team-color-swatch-${colorInfo.teamId}`}
        title={t("teamColors.tooltip", "Set team colors")}
        onClick={handleToggle}
        className="group relative flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
        aria-label={`${label} ${t("teamColors.tooltip", "Set team colors")}`}
      >
        <span
          data-testid={`team-color-swatch-${colorInfo.teamId}-dot`}
          aria-hidden="true"
          className="block h-4 w-4 rounded-full border border-slate-500 transition-colors group-hover:border-indigo-400"
          style={{
            background: colorInfo.primary_color ?? "#64748b",
          }}
        />
      </button>
      {open && anchorRect && (
        <TeamColorPicker
          teamId={colorInfo.teamId}
          initialPrimary={colorInfo.primary_color}
          initialSecondary={colorInfo.secondary_color}
          onClose={() => setOpen(false)}
          onSaved={handleSaved}
          t={t}
          anchorRect={anchorRect}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ChangeLastEventPlayerButton — PMA-COCK-099: one-tap re-assign of the
// player on the most recent goal/yellow/red card. Same popover pattern as
// TeamColorPicker/ColorSwatchButton above (createPortal, outside-click +
// Escape to close, anchored via getBoundingClientRect).
// ---------------------------------------------------------------------------

interface ChangeLastEventPlayerButtonProps {
  liveEvents: MatchEvent[];
  match: Match | null;
  onUpdateEventData: (
    event: MatchEvent,
    updates: Partial<MatchEvent>,
  ) => Promise<void> | void;
  disabled?: boolean;
  t: TFunction<"logger">;
}

const ChangeLastEventPlayerButton = ({
  liveEvents,
  match,
  onUpdateEventData,
  disabled,
  t,
}: ChangeLastEventPlayerButtonProps) => {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const targetEvent = useMemo(
    () => findLastPlayerGoalOrCardEvent(liveEvents),
    [liveEvents],
  );

  // Close on outside click OR Escape — identical timing/rationale to
  // TeamColorPicker above (deferred registration so the opening click
  // itself doesn't immediately close the popover).
  useEffect(() => {
    if (!open) return undefined;
    let timerId: ReturnType<typeof setTimeout>;

    const handlePointerDown = (e: PointerEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    timerId = setTimeout(() => {
      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("keydown", handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    if (!targetEvent) return;
    if (!open && buttonRef.current) {
      setAnchorRect(buttonRef.current.getBoundingClientRect());
      setSelectedPlayerId(targetEvent.player_id || "");
      setToast(null);
    }
    setOpen((v) => !v);
  }, [open, targetEvent]);

  const handleSave = async () => {
    if (!targetEvent || saving || !selectedPlayerId) return;
    if (selectedPlayerId === (targetEvent.player_id || "")) {
      setOpen(false);
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      await onUpdateEventData(targetEvent, { player_id: selectedPlayerId });
      setToast({ msg: t("saved", "Saved"), ok: true });
      setTimeout(() => setOpen(false), 600);
    } catch {
      setToast({
        msg: t("changeLastEventPlayerError", "Failed to reassign player"),
        ok: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const roster = targetEvent
    ? getTeamRosterForEvent(targetEvent.team_id, match)
    : [];

  const POPOVER_WIDTH = 224;
  const GAP = 6;
  const top = anchorRect ? anchorRect.bottom + GAP : 0;
  const left = anchorRect
    ? Math.min(anchorRect.left, window.innerWidth - POPOVER_WIDTH - 8)
    : 0;

  return (
    <div className="relative shrink-0">
      <button
        ref={buttonRef}
        type="button"
        data-testid="change-last-event-player-button"
        onClick={handleToggle}
        disabled={disabled || !targetEvent}
        title={
          targetEvent
            ? t(
                "changeLastEventPlayerTitle",
                "Reassign the player on the most recent goal, yellow or red card",
              )
            : t(
                "changeLastEventPlayerNone",
                "No recent goal/card event to reassign",
              )
        }
        className={`inline-flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          targetEvent
            ? "text-blue-300 border-blue-500/40 bg-blue-900/20 hover:bg-blue-900/40"
            : "text-slate-600 border-slate-700"
        }`}
      >
        <UserCog size={18} />
        {t("changeLastEventPlayer", "Change last event player")}
      </button>

      {open &&
        targetEvent &&
        createPortal(
          <div
            ref={popoverRef}
            data-testid="change-last-event-player-popover"
            style={{ position: "fixed", top, left, width: POPOVER_WIDTH }}
            className="z-[9999] bg-slate-900 border border-slate-600 rounded-xl shadow-2xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {t("changeLastEventPlayer", "Change last event player")}
            </p>
            <PlayerPicker
              players={roster}
              value={selectedPlayerId}
              onChange={setSelectedPlayerId}
              t={t}
              testId="change-last-event-player-select"
              requirePlayer
            />
            {toast && (
              <p
                className={`text-[11px] font-semibold ${
                  toast.ok ? "text-emerald-400" : "text-red-400"
                }`}
                role="status"
              >
                {toast.msg}
              </p>
            )}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                data-testid="change-last-event-player-cancel"
                onClick={() => setOpen(false)}
                disabled={saving}
                className="flex-1 text-xs py-1.5 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50 transition-colors"
              >
                {t("cancel", "Cancel")}
              </button>
              <button
                type="button"
                data-testid="change-last-event-player-save"
                onClick={handleSave}
                disabled={saving || !selectedPlayerId}
                className="flex-1 text-xs py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 transition-colors font-semibold"
              >
                {saving ? t("saving", "Saving…") : t("save", "Save")}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TeamSelector (main export)
// ---------------------------------------------------------------------------

const TeamSelector = ({
  isFlipped,
  onFlip,
  onUndo,
  undoDisabled,
  undoCount = 0,
  disabled = false,
  viewMode,
  setViewMode,
  homeTeamName,
  awayTeamName,
  homeTeamColorInfo,
  awayTeamColorInfo,
  onTeamColorSaved,
  match,
  liveEvents,
  onUpdateEventData,
  t,
}: TeamSelectorProps) => {
  const lastTouchUndoAt = useRef(0);

  const handleUndoClick = useCallback(() => {
    // Ignore synthetic click that follows a touch pointer interaction.
    if (Date.now() - lastTouchUndoAt.current < 700) {
      return;
    }
    onUndo();
  }, [onUndo]);

  const handleUndoPointerUp = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType !== "touch" || undoDisabled) return;
      lastTouchUndoAt.current = Date.now();
      onUndo();
    },
    [onUndo, undoDisabled],
  );

  // When the field is flipped, swap which team's name/color-swatch shows on
  // which side. (Formation-side swapping removed with the picker itself,
  // PMA-COCK-117.)
  const leftTeamName = isFlipped ? awayTeamName : homeTeamName;
  const rightTeamName = isFlipped ? homeTeamName : awayTeamName;
  const leftColorInfo = isFlipped ? awayTeamColorInfo : homeTeamColorInfo;
  const rightColorInfo = isFlipped ? homeTeamColorInfo : awayTeamColorInfo;

  return (
    <div className="bg-slate-800 rounded-lg shadow p-5 max-lg:p-1.5 border border-slate-700">
      <div className="flex flex-wrap items-center justify-center gap-3 gap-y-3 max-lg:gap-1.5 max-lg:gap-y-1.5">
        {/* Left team slot — name + color swatch. FormationPicker removed
            here (PMA-COCK-117); see the @deprecated block above. */}
        <div
          className="flex items-center gap-1.5 shrink-0"
          data-testid={`formation-slot-left`}
        >
          <span
            data-testid="formation-slot-left-name"
            className="text-xs font-semibold uppercase tracking-wider text-slate-400"
            style={{
              // EPIC-111 UIJ-R2-2: lightened to >= 4.5:1 on the slate-800 card.
              color: leftColorInfo?.secondary_color
                ? lightenForContrast(leftColorInfo.secondary_color)
                : undefined,
            }}
          >
            {leftTeamName}
          </span>
          {leftColorInfo && (
            <ColorSwatchButton
              colorInfo={leftColorInfo}
              label={leftTeamName ?? ""}
              onSaved={(primary, secondary) =>
                onTeamColorSaved?.(leftColorInfo.teamId, primary, secondary)
              }
              t={t}
            />
          )}
        </div>

        {/* Center controls: Flip + Undo.
            flex-wrap (not the outer row's own concern — this is scoped to
            THIS row only): at narrower available widths, notably once the
            view-mode toggle group and this row share the outer flex line
            (PMA-COCK-117 narrowed formation-slot-{left,right} enough that
            they now do, at some viewports, where before the wider
            FormationPicker chips forced the toggle group onto its own
            wrapped line), Flip + Undo + ChangeLastEventPlayerButton's
            combined natural width can exceed what's left for this `flex-1`
            row. Without wrap, `justify-center` on an overflowing flex line
            hangs the overflow off BOTH sides symmetrically, pushing the
            flip button behind `formation-slot-left` — a real, deterministic
            regression (confirmed via a baseline vs. current A/B measurement,
            not assumed). `flex-wrap` here lets ChangeLastEventPlayerButton
            (or Flip/Undo) drop to a second line WITHIN this row's own box
            instead — the row grows taller, not wider than its allotment —
            so it can never overlap formation-slot-{left,right} regardless
            of how tight the shared line gets. No visible change when
            everything fits on one line (the common case). */}
        <div className="flex flex-wrap gap-3 max-lg:gap-1.5 flex-1 justify-center">
          <button
            type="button"
            onClick={onFlip}
            disabled={disabled}
            data-testid="toggle-field-flip"
            className={`flex-1 max-w-[200px] inline-flex items-center justify-center gap-2.5 py-3.5 max-lg:min-h-11 max-lg:py-2 max-lg:text-sm max-lg:gap-1.5 rounded-lg text-base font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              disabled
                ? "text-slate-600 border border-slate-700"
                : isFlipped
                  ? "text-amber-200 border border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/20"
                  : "text-slate-300 border border-slate-600 bg-slate-700 hover:bg-slate-600"
            }`}
          >
            <ArrowLeftRight size={20} />
            {t("flipField", "Flip field")}
          </button>
          <button
            type="button"
            onClick={handleUndoClick}
            onPointerUp={handleUndoPointerUp}
            disabled={undoDisabled}
            data-testid="undo-button"
            className={`relative flex-1 max-w-[200px] inline-flex items-center justify-center gap-2.5 py-3.5 max-lg:min-h-11 max-lg:py-2 max-lg:text-sm max-lg:gap-1.5 rounded-lg text-base font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              undoDisabled
                ? "text-slate-600 border border-slate-700"
                : "text-rose-300 border border-rose-500/40 bg-rose-900/20 hover:bg-rose-900/40"
            } touch-manipulation`}
          >
            <CornerUpLeft size={20} />
            {t("undoLast", "Undo last")}
            {undoCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                {undoCount}
              </span>
            )}
          </button>
          {/* PMA-COCK-099: client's own suggestion — a quick way to
           * reassign the player on the last goal/card, next to Undo,
           * instead of the undo-and-recreate dance. Only renders once the
           * caller wires liveEvents + onUpdateEventData through. */}
          {liveEvents && onUpdateEventData && (
            <ChangeLastEventPlayerButton
              liveEvents={liveEvents}
              match={match ?? null}
              onUpdateEventData={onUpdateEventData}
              disabled={disabled}
              t={t}
            />
          )}
        </div>

        {/* Right team slot — name + color swatch. FormationPicker removed
            here (PMA-COCK-117); see the @deprecated block above. */}
        <div
          className="flex items-center gap-1.5 shrink-0"
          data-testid={`formation-slot-right`}
        >
          <span
            data-testid="formation-slot-right-name"
            className="text-xs font-semibold uppercase tracking-wider text-slate-400"
            style={{
              // EPIC-111 UIJ-R2-2: lightened to >= 4.5:1 on the slate-800 card.
              color: rightColorInfo?.secondary_color
                ? lightenForContrast(rightColorInfo.secondary_color)
                : undefined,
            }}
          >
            {rightTeamName}
          </span>
          {rightColorInfo && (
            <ColorSwatchButton
              colorInfo={rightColorInfo}
              label={rightTeamName ?? ""}
              onSaved={(primary, secondary) =>
                onTeamColorSaved?.(rightColorInfo.teamId, primary, secondary)
              }
              t={t}
            />
          )}
        </div>

        {/* View mode toggle: Logger / Review / Analytics */}
        {setViewMode && (
          <div className="flex rounded-lg border border-slate-600 overflow-hidden shrink-0">
            <button
              type="button"
              data-testid="toggle-logger-view"
              onClick={() => setViewMode("logger")}
              className={`inline-flex items-center gap-1.5 px-3 py-3 max-lg:min-h-11 max-lg:py-2 text-sm font-semibold transition-colors ${
                viewMode === "logger"
                  ? "bg-slate-700 text-slate-100"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              <List size={16} />
              <span className="max-lg:sr-only">
                {t("logger.view", "Logger")}
              </span>
            </button>
            <button
              type="button"
              data-testid="toggle-review"
              onClick={() => setViewMode("review")}
              className={`inline-flex items-center gap-1.5 px-3 py-3 max-lg:min-h-11 max-lg:py-2 text-sm font-semibold transition-colors ${
                viewMode === "review"
                  ? "bg-teal-600 text-white"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              <Eye size={16} />
              <span className="max-lg:sr-only">
                {t("logger.review", "Review")}
              </span>
            </button>
            <button
              type="button"
              data-testid="toggle-analytics"
              onClick={() => setViewMode("analytics")}
              className={`inline-flex items-center gap-1.5 px-3 py-3 max-lg:min-h-11 max-lg:py-2 text-sm font-semibold transition-colors ${
                viewMode === "analytics"
                  ? "bg-purple-600 text-white"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"
              }`}
            >
              <BarChart3 size={16} />
              <span className="max-lg:sr-only">
                {t("logger.analytics", "Analytics")}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSelector;
